import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Popover, type SxProps, type Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Icon from '/@/components/icon';
import Spinner from '/@/components/spinner';
import { _ } from '/@/locale';
import { trackEvent } from '/@/store/tracking';
import FrostRiskMapOverlay from './frost-risk-overlay';
import GDUForecast from './gdu-forecast';
import GDUMapOverlay from './gdu-overlay';
import FrostRiskMap from './map';
import MinTempForecastOverlay from './min-temp-forecast-overlay';

export const cardSx: SxProps<Theme> = {
    background: 'white',
    borderRadius: '10px',
    boxShadow: '-20px 10px 20px 0px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    margin: '12px',
};

const FrostRiskView = ({
    id,
    isHome = false,
    fields = [],
    selectedField,
    handleSelectField,
}: {
    id: number;
    isHome: boolean;
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [subActiveTab, setSubActiveTab] = useState(undefined);
    const [submenu, setSubmenu] = useState(undefined);
    const [tabProps, setTabProps] = useState(undefined);
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState({});

    const [map, setMap] = useState(undefined);
    const [anchorEl, setAnchorEl] = useState(null);

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const changeTab = (tabIndex, ev = undefined) => {
        const newTab = TAB_INFO[tabIndex];
        const sub = newTab.submenu;
        if (sub && ev) {
            setAnchorEl(ev.currentTarget);
            setSubmenu({ tab: tabIndex, submenu: sub });
        } else if (sub) {
            changeTabSubmenu(0, tabIndex);
        } else {
            setActiveTab(tabIndex);
            setTabProps({ ...newTab.props });
            setSubActiveTab(undefined);
            if (newTab.dispatchAction) {
                newTab.dispatchAction();
            }
        }
    };

    const changeTabSubmenu = (submenuIndex, tabI) => {
        const tabIndex = submenu ? submenu.tab : tabI;
        const newTab = TAB_INFO[tabIndex];
        const activeSubmenu = newTab.submenu[submenuIndex];
        setSubmenu(undefined);
        setActiveTab(tabIndex);
        setSubActiveTab(submenuIndex);
        setTabProps({ ...newTab.props, ...activeSubmenu.props });
        if (activeSubmenu.dispatchAction) {
            activeSubmenu.dispatchAction();
        }
    };

    useEffect(() => {
        if (id) {
            changeTab(id);
        }
    }, [id]);

    const TAB_INFO = [
        {
            showMap: true,
            label: _('Mapa de riesgo de helada'),
            overlay: <FrostRiskMapOverlay />,
            dispatchAction: () => {
                dispatch(trackEvent({ key: 'frost_risk.frost_risk.forecast' }));
            },
        },
        {
            showMap: true,
            label: _('Mapa GDUs acumulados'),
            overlay: <GDUMapOverlay />,
            submenu: [7, 14].map((days) => ({
                label: _('Mapa GDUs acumulados a {{dias}} días', { dias: days }),
                props: { days },
                dispatchAction: () => {
                    dispatch(trackEvent({ key: `frost_risk.frost_risk_map.GDU_${days}` }));
                },
            })),
        },
        {
            showMap: isHome,
            label: _('Pronóstico de temperaturas mínimas'),
            overlay: <MinTempForecastOverlay />,
            dispatchAction: () => {
                dispatch(trackEvent({ key: 'frost_risk.frost_risk_map.minimal_temp' }));
            },
        },
        {
            showMap: isHome,
            label: _('Pronóstico de GDUs'),
            overlay: <GDUForecast />,
            dispatchAction: () => {
                dispatch(trackEvent({ key: 'frost_risk.frost_risk_map.minimal_temp' }));
            },
        },
    ];

    const createOnChangeMapHandler = (tabIndex) => (map) => {
        const newMap = { ...map, tabIndex };
        setMap(newMap);
    };

    const selectedTab = TAB_INFO[activeTab];
    let selectedSubTab;
    if (subActiveTab !== undefined) selectedSubTab = selectedTab.submenu[subActiveTab];

    const createIsLoadingHandler = (tabIndex) => (bool) =>
        setIsLoading((prevIsLoading) => ({ ...prevIsLoading, [tabIndex]: bool }));
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {selectedTab.showMap && (
                <FrostRiskMap
                    fields={fields}
                    selectedMap={map}
                    setMapLoading={setIsLoading}
                    mapLoading={isLoading[activeTab]}
                    zoomControl={!isHome}
                    selectedField={selectedField}
                    handleSelectField={handleSelectField}
                />
            )}
            {isLoading[activeTab] && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '100px',
                        height: '100px',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                    }}
                >
                    <Spinner
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </Box>
            )}
            <Box
                sx={[
                    cardSx,
                    { width: { sm: 'fit-content' }, overflow: 'visible', zIndex: 1 },
                    !selectedTab.showMap && {
                        boxShadow: 'none',
                        border: '1px lightgray solid',
                    },
                    isMobile && {
                        m: 0,
                        border: 'none',
                        borderRadius: 0,
                        position: 'fixed',
                        bottom: 0,
                        textAlign: 'center',
                        height: '10%',
                        borderTop: '1px lightgray solid',
                    },
                ]}
            >
                {!isMobile && (
                    <Box sx={{ p: 1, borderRight: '1px lightgray solid' }}>
                        <Icon type='frostRisk' size='md' />
                    </Box>
                )}
                {TAB_INFO.map(({ label, submenu }, i) => (
                    <Box
                        sx={[
                            {
                                p: { xs: 0.5, md: 3 },
                                cursor: 'pointer',
                                height: '100%',
                                fontSize: { xs: 10, sm: 14 },
                            },
                            activeTab === i && {
                                borderBottom: (t) => `4px solid ${t.palette.primary.main}`,
                                pb: { xs: 0, md: 2.5 },
                            },
                        ]}
                        onClick={(ev) => changeTab(i, ev)}
                        key={i}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            {selectedSubTab && i === activeTab ? selectedSubTab.label : label}
                            {submenu && <KeyboardArrowDownIcon />}
                        </Box>
                    </Box>
                ))}
                {submenu && (
                    <Popover
                        open={submenu !== undefined}
                        anchorEl={anchorEl}
                        onClose={() => setSubmenu(undefined)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        sx={{ ...cardSx, background: 'none', m: 0, width: '100%' }}
                    >
                        {submenu.submenu.map(({ label }, i) => (
                            <Box sx={{ p: { xs: 1, md: 2 } }} key={i} onClick={() => changeTabSubmenu(i)}>
                                {label}
                            </Box>
                        ))}
                    </Popover>
                )}
            </Box>

            {isHome && (activeTab === 2 || activeTab === 3) ? (
                <Dialog
                    open
                    sx={{ width: '100%' }}
                    onClose={() => setActiveTab(0)}
                    PaperProps={{
                        sx: {
                            width: '100%',
                            position: 'relative',
                            left: { md: '10%' },
                        },
                    }}
                >
                    <DialogTitle disableTypography sx={{ p: 0, textAlign: 'end' }}>
                        <IconButton onClick={() => setActiveTab(0)}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <Box
                            sx={{
                                pointerEvents: 'none',
                            }}
                        >
                            {React.cloneElement(selectedTab.overlay, {
                                tabIndex: activeTab,
                                onChangeMap: createOnChangeMapHandler(activeTab),
                                map,
                                isHome,
                                ...tabProps,
                                isLoading: isLoading[activeTab],
                                setIsLoading: createIsLoadingHandler(activeTab),
                            })}
                        </Box>
                    </DialogContent>
                </Dialog>
            ) : (
                <Box
                    sx={{
                        height: '100%',
                        position: 'relative',
                        pointerEvents: 'none',
                    }}
                >
                    {React.cloneElement(selectedTab.overlay, {
                        tabIndex: activeTab,
                        onChangeMap: createOnChangeMapHandler(activeTab),
                        map,
                        ...tabProps,
                        isLoading: isLoading[activeTab],
                        setIsLoading: createIsLoadingHandler(activeTab),
                    })}
                </Box>
            )}
        </Box>
    );
};

export default FrostRiskView;
